import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, IconButton, Typography, TablePagination, TextField, useTheme, useMediaQuery, Card, CardContent, Stack, InputAdornment, Tooltip, Tabs, Tab
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import carteraService, { Cartera, CarteraPagedResult } from '../../services/carteraService';
import companiaService, { Compania } from '../../services/companiaService';
import {
  loadTesoreriaFiltros,
  storedNumber,
  storedString,
  TESORERIA_FILTROS_KEYS,
  usePersistTesoreriaFiltros,
} from '../../utils/tesoreriaFiltrosStorage';

interface CarterasFiltrosStored {
  searchTerm?: string;
  rowsPerPage?: number;
  tipoCuenta?: string;
}

const TIPO_CUENTA_TODOS = 'Todos';

const textoCelda = (value: unknown): string => {
  const t = String(value ?? '').trim();
  return t || '-';
};

const labelCompania = (
  idCompania: string | null | undefined,
  mapa: Map<string, string>,
): string => {
  const id = String(idCompania ?? '').trim();
  if (!id) return '-';
  const nombre = mapa.get(id);
  return nombre ? `${id} — ${nombre}` : id;
};

const Carteras: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const loadStored = () => loadTesoreriaFiltros<CarterasFiltrosStored>(TESORERIA_FILTROS_KEYS.carteras);

  const [carterasData, setCarterasData] = useState<CarteraPagedResult>({
    items: [], totalCount: 0, pageNumber: 1, pageSize: 10, totalPages: 0
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => storedNumber(loadStored(), 'rowsPerPage', 10));
  const [searchTerm, setSearchTerm] = useState(() => storedString(loadStored(), 'searchTerm', ''));
  const [tipoCuenta, setTipoCuenta] = useState(() =>
    storedString(loadStored(), 'tipoCuenta', TIPO_CUENTA_TODOS),
  );
  const [companias, setCompanias] = useState<Compania[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState(false);

  const companiasPorId = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of companias) {
      const id = String(c.idCompania ?? '').trim();
      if (id) map.set(id, String(c.nombre ?? '').trim());
    }
    return map;
  }, [companias]);

  const tipoCuentaApi = (tipo?: string) => {
    const value = tipo !== undefined ? tipo : tipoCuenta;
    return value === TIPO_CUENTA_TODOS ? undefined : value;
  };

  useEffect(() => {
    (async () => {
      try {
        const items = await companiaService.getCompanias({ pageNumber: 1, pageSize: 200 });
        setCompanias(items);
      } catch {
        setCompanias([]);
      }
    })();
  }, []);

  const fetchCarteras = async (
    pageNumber: number,
    pageSize: number,
    search?: string,
    tipo?: string,
  ) => {
    try {
      setLoading(true);
      const rawSearch = (search ?? '').trim();
      const compact = rawSearch.replace(/\s+/g, '');
      const isNumericSearch = compact !== '' && /^\d+$/.test(compact);
      const tipoFiltro = tipoCuentaApi(tipo);
      
      if (isNumericSearch) {
        try {
          const nro = Number(compact);
          const cartera = await carteraService.getCartera(nro);
          const tipoCartera = String(cartera?.tipoDeCuenta ?? '').trim();
          const coincideTipo = !tipoFiltro || tipoCartera === tipoFiltro;
          const item = cartera && coincideTipo ? cartera : null;
          const adjustedData = {
            items: item ? [item] : [],
            totalCount: item ? 1 : 0,
            pageNumber: 1,
            pageSize,
            totalPages: 1
          };
          setCarterasData(adjustedData);
          return;
        } catch (error: any) {
          if (error?.response?.status === 404) {
            setCarterasData({ items: [], totalCount: 0, pageNumber: 1, pageSize, totalPages: 0 });
            return;
          }
          console.error('Error al buscar por nroBanco:', error);
          setCarterasData({ items: [], totalCount: 0, pageNumber: 1, pageSize, totalPages: 0 });
          return;
        }
      }
      
      const response = await carteraService.getCarteras({
        pageNumber: pageNumber,
        pageSize,
        searchTerm: rawSearch || undefined,
        tipoDeCuenta: tipoFiltro,
      });
      setCarterasData(response);
    } catch (error: any) {
      console.error('Error al cargar carteras:', error);
      enqueueSnackbar('Error al cargar carteras', { variant: 'error' });
      setCarterasData({ items: [], totalCount: 0, pageNumber: 1, pageSize, totalPages: 0 });
    } finally {
      setLoading(false);
    }
  };

  usePersistTesoreriaFiltros(
    TESORERIA_FILTROS_KEYS.carteras,
    { searchTerm, rowsPerPage, tipoCuenta },
    [searchTerm, rowsPerPage, tipoCuenta]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchCarteras(page + 1, rowsPerPage, searchTerm, tipoCuenta);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchTerm, tipoCuenta]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleTipoCuentaChange = (_event: React.SyntheticEvent, newValue: string) => {
    setTipoCuenta(newValue);
    setPage(0);
  };

  const handleDelete = async (nroBanco: number) => {
    if (window.confirm('¿Está seguro de eliminar esta cartera?')) {
      try {
        await carteraService.deleteCartera(nroBanco);
        enqueueSnackbar('Cartera eliminada exitosamente', { variant: 'success' });
        fetchCarteras(page + 1, rowsPerPage, searchTerm, tipoCuenta);
      } catch (error: any) {
        console.error('Error al eliminar cartera:', error);
        enqueueSnackbar(error?.response?.data?.message || 'Error al eliminar cartera', { variant: 'error' });
      }
    }
  };

  const renderDetalleCampos = (cartera: Cartera) => (
    <>
      <Typography variant="body2" color="text.secondary">
        Tipo: {textoCelda(cartera.tipoDeCuenta)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        N° Cuenta: {textoCelda(cartera.nroDeCuenta)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        CBU: {textoCelda(cartera.cbu)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Titular: {textoCelda(cartera.titular)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Sucursal: {textoCelda(cartera.sucursal)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Cta. contable: {cartera.nroCtaCble != null && Number(cartera.nroCtaCble) > 0 ? cartera.nroCtaCble : '-'}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Compañía: {labelCompania(cartera.idCompania, companiasPorId)}
      </Typography>
    </>
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between" 
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: '#1976d2'
              }}
            >
              Carteras
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/tesoreria/carteras/nuevo')}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                }
              }}
            >
              Nueva Cartera
            </Button>
          </Stack>

          <Box sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tipoCuenta}
              onChange={handleTipoCuentaChange}
              aria-label="filtro tipo de cuenta"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  minHeight: 48,
                  minWidth: 'auto',
                  px: { xs: 1.5, sm: 2 },
                },
              }}
            >
              <Tab label="Todos" value={TIPO_CUENTA_TODOS} />
              <Tab label="Cuenta Corriente" value="Cuenta Corriente" />
              <Tab label="Caja de Ahorro" value="Caja de Ahorro" />
              <Tab label="Cartera" value="Cartera" />
            </Tabs>
          </Box>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por nombre o número de cartera..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#fafafa',
                  '&:hover': {
                    bgcolor: '#f5f5f5',
                  },
                  '&.Mui-focused': {
                    bgcolor: '#ffffff',
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {isMobile ? (
            <Stack spacing={2}>
              {carterasData.items.map((cartera) => (
                <Card 
                  key={cartera.nroBanco}
                  sx={{
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {cartera.nombreBanco?.trim() || '-'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Nro: {cartera.nroBanco}
                          </Typography>
                        </Box>
                      </Stack>
                      <Stack spacing={0.5}>
                        {renderDetalleCampos(cartera)}
                      </Stack>
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Tooltip title="Editar cartera">
                          <IconButton
                            onClick={() => navigate(`/tesoreria/carteras/${cartera.nroBanco}`)}
                            size="small"
                            sx={{
                              bgcolor: '#2196f3',
                              color: '#fff',
                              borderRadius: 1.5,
                              '&:hover': { bgcolor: '#1976d2' }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar cartera">
                          <IconButton
                            onClick={() => handleDelete(cartera.nroBanco)}
                            size="small"
                            sx={{
                              bgcolor: '#f44336',
                              color: '#fff',
                              borderRadius: 1.5,
                              '&:hover': { bgcolor: '#d32f2f' }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
              <TablePagination
                component="div"
                count={carterasData.totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
              />
            </Stack>
          ) : (
            <TableContainer 
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
                overflowX: 'auto',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Nro. Cartera</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Tipo de cuenta</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>N° Cuenta</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>CBU</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Titular</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Sucursal</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Cta. contable</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Compañía</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {carterasData.items.map((cartera) => (
                    <TableRow 
                      key={cartera.nroBanco}
                      sx={{
                        '&:hover': {
                          bgcolor: '#f9f9f9',
                        },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>{cartera.nroBanco}</TableCell>
                      <TableCell>{cartera.nombreBanco?.trim() || '-'}</TableCell>
                      <TableCell>{textoCelda(cartera.tipoDeCuenta)}</TableCell>
                      <TableCell>{textoCelda(cartera.nroDeCuenta)}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {textoCelda(cartera.cbu)}
                      </TableCell>
                      <TableCell>{textoCelda(cartera.titular)}</TableCell>
                      <TableCell>{textoCelda(cartera.sucursal)}</TableCell>
                      <TableCell>
                        {cartera.nroCtaCble != null && Number(cartera.nroCtaCble) > 0
                          ? cartera.nroCtaCble
                          : '-'}
                      </TableCell>
                      <TableCell>{labelCompania(cartera.idCompania, companiasPorId)}</TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="Editar cartera">
                            <IconButton
                              onClick={() => navigate(`/tesoreria/carteras/${cartera.nroBanco}`)}
                              size="small"
                              sx={{
                                bgcolor: '#2196f3',
                                color: '#fff',
                                borderRadius: 1.5,
                                width: 32,
                                height: 32,
                                '&:hover': { 
                                  bgcolor: '#1976d2',
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s'
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar cartera">
                            <IconButton
                              onClick={() => handleDelete(cartera.nroBanco)}
                              size="small"
                              sx={{
                                bgcolor: '#f44336',
                                color: '#fff',
                                borderRadius: 1.5,
                                width: 32,
                                height: 32,
                                '&:hover': { 
                                  bgcolor: '#d32f2f',
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s'
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={carterasData.totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
                sx={{
                  borderTop: '1px solid #e0e0e0',
                }}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Carteras;

